// Insurance-carrier claim-help cluster (hub-and-spoke).
// Pillar: /florida-insurance-carrier-claim-help  +  10 carrier spokes.
// Driven by CarrierPage.jsx. High commercial intent: homeowners actively
// disputing a specific carrier's offer or denial.
//
// FDUTPA / advertising-compliance note: copy is factual, educational, and
// carrier-neutral about wrongdoing. We describe how claims *can* be underpaid
// or denied in general and how a licensed public adjuster helps — we do NOT
// assert that any named carrier acts in bad faith. Statute/deadline references
// are educational, not legal advice.

export const carriers = {
  "florida-insurance-carrier-claim-help": {
    slug: "florida-insurance-carrier-claim-help",
    isPillar: true,
    carrier: "Florida Insurance Carriers",
    topic: "By Carrier",
    h1: "Public Adjuster for Any Florida Insurance Carrier",
    metaTitle:
      "Public Adjuster for Florida Insurance Carriers | Denied & Underpaid Claim Help | Claim Beast",
    metaDescription:
      "Fighting a lowball offer or denial from Citizens, Universal, State Farm, Tower Hill, Heritage, Slide, Kin and more? Claim Beast public adjusters in Riverview, FL negotiate with every major Florida carrier. Free claim review.",
    keywords:
      "public adjuster florida insurance company, florida insurance carrier claim dispute, denied insurance claim florida carrier, underpaid claim public adjuster florida, insurance company lowball offer florida",
    intro:
      "Every Florida homeowners carrier runs its claims department differently — different adjusters, different estimating software, different reasons a claim gets underpaid or denied. What stays the same is the imbalance: the carrier has a team of adjusters and engineers working to control the payout, and you have a claim you've never filed before. Claim Beast levels the field. We're licensed Florida public adjusters who negotiate with every major carrier writing policies in the state, and we know how each one scopes, depreciates, and disputes a claim. Whether your offer came in low or your claim was denied outright, a free review costs you nothing.",
    points: [
      "Independent, replacement-cost estimates that stand up to the carrier's number",
      "A line-by-line rebuttal of underscoped or improperly depreciated items",
      "Documentation of hidden and code-related damage the carrier missed",
      "Direct negotiation, supplements, and escalation to appraisal or mediation",
      "One point of contact who handles the carrier so you don't have to",
    ],
    denialReasons: [
      "Lowball or underscoped estimates that leave out labor, overhead, or code upgrades",
      "Excessive or improper depreciation applied to recoverable items",
      "Cause-of-loss disputes (wear-and-tear vs. covered event)",
      "Missed or hidden damage the field adjuster didn't document",
      "Late-notice, exclusion, or documentation arguments used to reduce or deny payment",
    ],
    faqs: [
      {
        question: "Can a public adjuster help no matter which carrier I have?",
        answer:
          "Yes. We represent homeowners against every major carrier writing policies in Florida — from state-backed Citizens to national and Florida-domestic insurers. Our role is the same regardless of carrier: document the full loss, build a defensible estimate, and negotiate the fair payout your policy owes.",
      },
      {
        question: "My carrier already paid me. Is it too late to dispute the amount?",
        answer:
          "Often no. An initial payment is not a final settlement. If the check didn't reflect the true cost to repair, we can prepare and file a supplement. Florida also has deadlines to reopen or supplement a claim, so it's worth a free review as soon as you suspect the payment was short.",
      },
      {
        question: "Will hiring a public adjuster upset my carrier or hurt my policy?",
        answer:
          "Florida law gives you the right to hire your own licensed public adjuster, and using one is a normal part of the claims process. We handle communication professionally and keep everything focused on getting the claim documented and paid correctly.",
      },
      {
        question: "What does a public adjuster cost in Florida?",
        answer:
          "We work on contingency — no upfront cost. Florida caps public adjuster fees (generally up to 20% of the recovery, and 10% for claims tied to a declared emergency during the first year). If we don't recover for you, you don't pay a fee.",
      },
      {
        question: "How do I start?",
        answer:
          "Send us your denial letter or estimate and we'll review it for free. We'll tell you honestly whether the offer looks short and what we think the claim should be worth.",
      },
    ],
  },

  "citizens-insurance-claim-help-florida": {
    slug: "citizens-insurance-claim-help-florida",
    carrier: "Citizens Property Insurance",
    topic: "Citizens",
    h1: "Citizens Property Insurance Claim Help in Florida",
    metaTitle:
      "Citizens Property Insurance Claim Denied or Underpaid? | Florida Public Adjuster | Claim Beast",
    metaDescription:
      "Citizens Property Insurance claim underpaid or denied? Claim Beast public adjusters in Riverview, FL document the full loss and negotiate a fair payout from Florida's state-backed insurer. Free claim review.",
    keywords:
      "citizens property insurance claim denied, citizens insurance underpaid claim florida, citizens claim public adjuster, citizens property insurance dispute florida",
    intro:
      "Citizens Property Insurance is Florida's state-created, not-for-profit insurer of last resort — and for hundreds of thousands of Tampa Bay homeowners it's the only coverage available. Because Citizens is designed to keep costs contained, its claim estimates are often conservative, and homeowners frequently tell us the offer didn't come close to the real cost of repairs. Claim Beast documents the full scope of your loss, builds an independent replacement-cost estimate, and negotiates directly with Citizens so your payout reflects what it actually costs to make your home whole.",
    points: [
      "Independent replacement-cost estimate to compare against the Citizens offer",
      "Documentation of hidden, code-required, and matching-related damage",
      "Rebuttal of conservative scoping and improper depreciation",
      "Supplement filing when the first payment falls short",
      "Direct negotiation and escalation through the Citizens dispute process",
    ],
    denialReasons: [
      "Conservative field estimates that underscope the repair",
      "Depreciation that isn't properly recovered",
      "Cause-of-loss disputes on roof and water claims",
      "Managed-repair or preferred-vendor pressure that caps the payout",
      "Missed code-upgrade and matching costs required to complete the repair",
    ],
    faqs: [
      {
        question: "Why is my Citizens claim offer so low?",
        answer:
          "Citizens is a not-for-profit insurer of last resort, so its estimates tend to be conservative. That doesn't mean the low number is correct — it means the scope and pricing should be checked line by line. We frequently find missing items, undervalued pricing, and unrecovered depreciation.",
      },
      {
        question: "Can I dispute a Citizens claim decision?",
        answer:
          "Yes. You can supplement an underpaid claim and dispute a denial. Citizens has its own claims and appraisal procedures, and we prepare the documentation and independent estimate needed to move the number.",
      },
      {
        question: "Does Citizens use managed repair?",
        answer:
          "Citizens has used managed-repair programs on certain claims. You still have the right to understand the full value of your loss and to have a public adjuster document it, so you can make an informed decision rather than accepting a capped scope.",
      },
      {
        question: "Is there a deadline to reopen my Citizens claim?",
        answer:
          "Florida law sets deadlines to file, supplement, and reopen property claims. Those windows have tightened in recent years, so the sooner you have your paperwork reviewed, the better. This is general information, not legal advice.",
      },
    ],
  },

  "universal-property-insurance-claim-help-florida": {
    slug: "universal-property-insurance-claim-help-florida",
    carrier: "Universal Property & Casualty Insurance",
    topic: "Universal",
    h1: "Universal Property & Casualty Claim Help in Florida",
    metaTitle:
      "Universal Property Insurance Claim Denied or Underpaid? | Florida Public Adjuster | Claim Beast",
    metaDescription:
      "Universal Property & Casualty claim underpaid or denied in Florida? Claim Beast public adjusters in Riverview, FL rebuild the estimate and negotiate the payout you're owed. Free claim review.",
    keywords:
      "universal property insurance claim denied, universal property casualty underpaid claim, universal insurance public adjuster florida, universal property claim dispute",
    intro:
      "Universal Property & Casualty is one of the largest writers of Florida homeowners policies, insuring a huge number of Tampa Bay properties. With that volume comes fast, high-throughput claims handling — and homeowners often tell us the field estimate missed damage or came in well below the cost of a proper repair. Claim Beast re-inspects the loss, prepares an independent replacement-cost estimate, and negotiates with Universal so the settlement reflects the true scope of the work.",
    points: [
      "Full re-inspection and independent estimate of the loss",
      "Line-by-line comparison against the Universal offer",
      "Documentation of overlooked, hidden, and code-related damage",
      "Proper handling of depreciation and recoverable holdback",
      "Supplements, disputes, and escalation when the offer is short",
    ],
    denialReasons: [
      "High-volume field estimates that underscope the damage",
      "Improper or excessive depreciation",
      "Wear-and-tear vs. covered-event cause disputes",
      "Roof and water sublimits or exclusions applied aggressively",
      "Documentation and inspection gaps that suppress the payout",
    ],
    faqs: [
      {
        question: "My Universal estimate seems low — what can I do?",
        answer:
          "Get it reviewed. We prepare an independent estimate and compare it against Universal's scope and pricing. If items are missing or undervalued, we document them and file a supplement.",
      },
      {
        question: "Universal denied my roof claim as wear and tear. Is that final?",
        answer:
          "Not necessarily. Cause-of-loss determinations can be challenged with proper documentation of storm or event-related damage. We inspect, document objective evidence, and dispute the denial reason directly.",
      },
      {
        question: "Do you handle Universal water damage claims?",
        answer:
          "Yes. Water losses are among the most commonly underpaid claims because of hidden damage behind walls and under floors. We document the full extent — including mold and code-required work — so it isn't left off the estimate.",
      },
      {
        question: "How fast can you review my Universal claim?",
        answer:
          "Send us the estimate or denial letter and we'll turn around a free review quickly and tell you whether it looks short.",
      },
    ],
  },

  "state-farm-insurance-claim-help-florida": {
    slug: "state-farm-insurance-claim-help-florida",
    carrier: "State Farm Florida",
    topic: "State Farm",
    h1: "State Farm Claim Help for Florida Homeowners",
    metaTitle:
      "State Farm Claim Denied or Underpaid in Florida? | Public Adjuster | Claim Beast",
    metaDescription:
      "State Farm homeowners claim underpaid or denied in Florida? Claim Beast public adjusters in Riverview, FL document the full loss and negotiate a fair settlement. Free claim review.",
    keywords:
      "state farm claim denied florida, state farm underpaid claim, state farm public adjuster florida, state farm homeowners claim dispute tampa",
    intro:
      "State Farm is one of the most recognized names in homeowners insurance, and it writes a large book of Florida policies. Big carriers rely on standardized estimating and experienced adjusters, which is exactly why a homeowner's own documentation matters — a single missed line item or an aggressive depreciation entry can cost thousands. Claim Beast prepares an independent, defensible estimate and negotiates with State Farm so your settlement covers the real cost of repairs, not just the first number offered.",
    points: [
      "Independent replacement-cost estimate to test the State Farm offer",
      "Detailed documentation of the full scope of damage",
      "Rebuttal of underscoped items and improper depreciation",
      "Supplement filing for damage found after the first payment",
      "Direct negotiation and escalation to appraisal when warranted",
    ],
    denialReasons: [
      "Standardized estimates that miss property-specific damage",
      "Depreciation applied to items that should be recoverable",
      "Cause-of-loss and pre-existing-damage disputes",
      "Partial denials that pay some damage but exclude related work",
      "Coverage or exclusion arguments used to reduce the claim",
    ],
    faqs: [
      {
        question: "Can a public adjuster negotiate with State Farm?",
        answer:
          "Yes. As your licensed representative we communicate and negotiate with State Farm on your behalf, backed by an independent estimate and full documentation of the loss.",
      },
      {
        question: "State Farm paid part of my claim — can I get more?",
        answer:
          "Often yes. A partial payment isn't the ceiling. If related or hidden damage was excluded, we document it and file a supplement to recover it.",
      },
      {
        question: "Should I use appraisal on my State Farm claim?",
        answer:
          "Appraisal is a common way to resolve a value dispute when the policy allows it. We help you decide whether appraisal, negotiation, or mediation is the right path and prepare the estimate to support it.",
      },
      {
        question: "What will this cost me?",
        answer:
          "Nothing upfront. We work on contingency within Florida's fee caps and only earn if we recover more for you.",
      },
    ],
  },

  "tower-hill-insurance-claim-help-florida": {
    slug: "tower-hill-insurance-claim-help-florida",
    carrier: "Tower Hill Insurance",
    topic: "Tower Hill",
    h1: "Tower Hill Insurance Claim Help in Florida",
    metaTitle:
      "Tower Hill Insurance Claim Denied or Underpaid? | Florida Public Adjuster | Claim Beast",
    metaDescription:
      "Tower Hill Insurance claim underpaid or denied in Florida? Claim Beast public adjusters in Riverview, FL rebuild the estimate and fight for the payout you're owed. Free claim review.",
    keywords:
      "tower hill insurance claim denied, tower hill underpaid claim florida, tower hill public adjuster, tower hill claim dispute florida",
    intro:
      "Tower Hill is a long-standing Florida-domestic insurer covering many Tampa Bay homes. Florida-domestic carriers face intense cost pressure after major storm seasons, and that pressure can show up as conservative estimates and disputed causes of loss. Claim Beast re-documents your damage from the ground up, prepares an independent estimate, and negotiates with Tower Hill so your payout matches the true cost to repair.",
    points: [
      "Independent, replacement-cost estimate of the full loss",
      "Documentation of hidden, matching, and code-required damage",
      "Rebuttal of conservative scoping and depreciation",
      "Supplement and dispute filing when payment falls short",
      "Negotiation and escalation to appraisal or mediation",
    ],
    denialReasons: [
      "Conservative estimates that underscope repairs",
      "Improper depreciation and unrecovered holdback",
      "Roof age and wear-and-tear cause disputes",
      "Water and mold sublimits applied aggressively",
      "Documentation gaps from the initial inspection",
    ],
    faqs: [
      {
        question: "Tower Hill's estimate is lower than my contractor's. Why?",
        answer:
          "Carrier estimates and contractor bids use different pricing and scope assumptions. We reconcile the two with an independent estimate and document any items the carrier left out so the numbers can be compared fairly.",
      },
      {
        question: "Can you dispute a Tower Hill roof denial?",
        answer:
          "Yes. Roof denials often turn on cause and age. We document event-related damage with objective evidence and dispute the stated denial reason.",
      },
      {
        question: "Is it worth reopening an old Tower Hill claim?",
        answer:
          "If the payment didn't cover the real repair and you're within Florida's supplement/reopen deadlines, it can be. A free review will tell you where you stand.",
      },
      {
        question: "How do public adjuster fees work here?",
        answer:
          "Contingency only, within Florida's statutory caps. No recovery, no fee.",
      },
    ],
  },

  "heritage-insurance-claim-help-florida": {
    slug: "heritage-insurance-claim-help-florida",
    carrier: "Heritage Insurance",
    topic: "Heritage",
    h1: "Heritage Insurance Claim Help in Florida",
    metaTitle:
      "Heritage Insurance Claim Denied or Underpaid? | Florida Public Adjuster | Claim Beast",
    metaDescription:
      "Heritage Insurance claim underpaid or denied in Florida? Claim Beast public adjusters in Riverview, FL document the full loss and negotiate the settlement you're owed. Free claim review.",
    keywords:
      "heritage insurance claim denied, heritage property insurance underpaid, heritage public adjuster florida, heritage insurance claim dispute",
    intro:
      "Heritage Insurance writes a significant volume of Florida homeowners policies, including many in the Tampa Bay area. As with other Florida-domestic carriers, homeowners often see estimates that don't fully account for hidden damage, code upgrades, or the cost to match materials. Claim Beast re-inspects the loss, builds an independent estimate, and negotiates with Heritage so the settlement reflects the full, correct scope.",
    points: [
      "Full re-inspection and independent estimate",
      "Documentation of hidden and code-required damage",
      "Proper depreciation and recoverable holdback handling",
      "Matching and finish-out costs the carrier may omit",
      "Supplements, disputes, and escalation as needed",
    ],
    denialReasons: [
      "Underscoped field estimates",
      "Excessive depreciation",
      "Cause-of-loss disputes on roof and water claims",
      "Sublimits and exclusions applied to reduce payment",
      "Incomplete initial documentation",
    ],
    faqs: [
      {
        question: "My Heritage payout won't cover my repairs. What now?",
        answer:
          "We prepare an independent estimate, identify what's missing from Heritage's scope, and file a supplement to recover the difference.",
      },
      {
        question: "Can you handle a Heritage water damage claim?",
        answer:
          "Yes. Water losses hide damage behind walls and under floors, and we document the full extent — including any required mold remediation — so it's included in the claim.",
      },
      {
        question: "What if Heritage denied my claim entirely?",
        answer:
          "We review the denial letter and policy, document the covered cause of loss, and dispute the denial with supporting evidence.",
      },
      {
        question: "Do I pay anything upfront?",
        answer:
          "No. We work on contingency within Florida's fee caps.",
      },
    ],
  },

  "american-integrity-insurance-claim-help-florida": {
    slug: "american-integrity-insurance-claim-help-florida",
    carrier: "American Integrity Insurance",
    topic: "American Integrity",
    h1: "American Integrity Insurance Claim Help in Florida",
    metaTitle:
      "American Integrity Insurance Claim Denied or Underpaid? | Florida Public Adjuster | Claim Beast",
    metaDescription:
      "American Integrity Insurance claim underpaid or denied in Florida? Claim Beast public adjusters in Riverview, FL rebuild the estimate and negotiate a fair payout. Free claim review.",
    keywords:
      "american integrity insurance claim denied, american integrity underpaid claim, american integrity public adjuster florida, american integrity claim dispute",
    intro:
      "American Integrity Insurance is a Florida-focused homeowners carrier covering many properties across Tampa Bay. Homeowners filing claims with any specialty Florida insurer often benefit from an independent estimate, because the carrier's scope and the true cost of repair don't always line up. Claim Beast documents the full loss, prepares a defensible estimate, and negotiates with American Integrity on your behalf.",
    points: [
      "Independent estimate to compare against the carrier's offer",
      "Documentation of hidden, matching, and code-related damage",
      "Rebuttal of underscoped and improperly depreciated items",
      "Supplement filing after the first payment",
      "Negotiation and escalation to appraisal or mediation",
    ],
    denialReasons: [
      "Underscoped estimates",
      "Improper depreciation",
      "Cause-of-loss disputes",
      "Aggressively applied sublimits or exclusions",
      "Gaps in the initial inspection and documentation",
    ],
    faqs: [
      {
        question: "How do I know if American Integrity underpaid me?",
        answer:
          "Compare the carrier's scope against an independent estimate. We do this for free and tell you whether the offer looks short and by roughly how much.",
      },
      {
        question: "Can you reopen a closed American Integrity claim?",
        answer:
          "If you're within Florida's supplement and reopen deadlines and the payment didn't cover the repair, yes — we can file a supplement.",
      },
      {
        question: "Will a public adjuster slow down my claim?",
        answer:
          "Our goal is the opposite: thorough documentation up front tends to reduce back-and-forth and move the claim toward a correct number faster.",
      },
      {
        question: "What are your fees?",
        answer:
          "Contingency only, within Florida's statutory caps. No recovery, no fee.",
      },
    ],
  },

  "progressive-home-insurance-claim-help-florida": {
    slug: "progressive-home-insurance-claim-help-florida",
    carrier: "Progressive Home (ASI)",
    topic: "Progressive Home",
    h1: "Progressive Home Insurance Claim Help in Florida",
    metaTitle:
      "Progressive Home Insurance Claim Denied or Underpaid? | Florida Public Adjuster | Claim Beast",
    metaDescription:
      "Progressive Home (ASI) homeowners claim underpaid or denied in Florida? Claim Beast public adjusters in Riverview, FL document the loss and negotiate your payout. Free claim review.",
    keywords:
      "progressive home insurance claim denied, progressive asi claim underpaid florida, progressive public adjuster florida, progressive homeowners claim dispute",
    intro:
      "Progressive's Florida homeowners policies are written through its ASI companies and cover a large number of Tampa Bay homes. National-scale claims handling relies on standardized estimating, which means property-specific damage can be missed and depreciation can be applied aggressively. Claim Beast prepares an independent estimate and negotiates with Progressive Home so your settlement reflects the actual cost to repair your property.",
    points: [
      "Independent replacement-cost estimate",
      "Documentation of property-specific and hidden damage",
      "Rebuttal of standardized underscoping and depreciation",
      "Supplements for damage found after the first payment",
      "Negotiation and escalation when the offer is short",
    ],
    denialReasons: [
      "Standardized estimates that miss property-specific damage",
      "Improper or excessive depreciation",
      "Cause-of-loss and pre-existing-damage disputes",
      "Partial denials that exclude related work",
      "Coverage and exclusion arguments",
    ],
    faqs: [
      {
        question: "Is Progressive Home the same as ASI?",
        answer:
          "Progressive writes many Florida homeowners policies through its American Strategic Insurance (ASI) companies. We handle claims under those policies the same way — with an independent estimate and full documentation.",
      },
      {
        question: "My Progressive estimate left out damage. Can that be fixed?",
        answer:
          "Yes. We document the missing items and file a supplement so the omitted damage is added back into the claim.",
      },
      {
        question: "Can you dispute a Progressive denial?",
        answer:
          "Yes. We review the denial and policy, document the covered loss, and dispute the stated reason with evidence.",
      },
      {
        question: "What does it cost?",
        answer:
          "Nothing upfront — contingency only, within Florida's fee caps.",
      },
    ],
  },

  "slide-insurance-claim-help-florida": {
    slug: "slide-insurance-claim-help-florida",
    carrier: "Slide Insurance",
    topic: "Slide",
    h1: "Slide Insurance Claim Help in Florida",
    metaTitle:
      "Slide Insurance Claim Denied or Underpaid? | Florida Public Adjuster | Claim Beast",
    metaDescription:
      "Slide Insurance claim underpaid or denied in Florida? Claim Beast public adjusters in Riverview, FL rebuild the estimate and negotiate the payout you're owed. Free claim review.",
    keywords:
      "slide insurance claim denied, slide insurance underpaid claim florida, slide public adjuster florida, slide homeowners claim dispute",
    intro:
      "Slide is a fast-growing, technology-driven Florida homeowners insurer that has taken on a large number of policies across the state, including many in Tampa Bay. Newer, data-heavy carriers often lean on automated and standardized estimating, and homeowners tell us the scope sometimes misses real-world repair costs. Claim Beast documents your loss in detail, builds an independent estimate, and negotiates with Slide so your payout reflects the true cost of repair.",
    points: [
      "Independent, replacement-cost estimate of the full loss",
      "Documentation of hidden and code-required damage",
      "Rebuttal of automated or standardized underscoping",
      "Proper depreciation and recoverable holdback handling",
      "Supplements, disputes, and escalation as needed",
    ],
    denialReasons: [
      "Automated or standardized estimates that underscope repairs",
      "Improper depreciation",
      "Roof and water cause-of-loss disputes",
      "Sublimits and exclusions applied aggressively",
      "Incomplete initial documentation",
    ],
    faqs: [
      {
        question: "Slide's estimate came from a quick inspection. Is it accurate?",
        answer:
          "Fast or automated inspections can miss damage. We perform a thorough re-inspection and prepare an independent estimate so anything left out is documented and added back.",
      },
      {
        question: "Can you dispute a Slide claim decision?",
        answer:
          "Yes. Whether the claim was underpaid or denied, we build the documentation and estimate needed to dispute and negotiate the number.",
      },
      {
        question: "Do you handle Slide roof claims?",
        answer:
          "Yes. Roof claims are frequently disputed on cause and age, and we document event-related damage with objective evidence.",
      },
      {
        question: "What are your fees?",
        answer:
          "Contingency only, within Florida's statutory caps. No recovery, no fee.",
      },
    ],
  },

  "kin-insurance-claim-help-florida": {
    slug: "kin-insurance-claim-help-florida",
    carrier: "Kin Insurance",
    topic: "Kin",
    h1: "Kin Insurance Claim Help in Florida",
    metaTitle:
      "Kin Insurance Claim Denied or Underpaid? | Florida Public Adjuster | Claim Beast",
    metaDescription:
      "Kin Insurance claim underpaid or denied in Florida? Claim Beast public adjusters in Riverview, FL document the full loss and negotiate a fair settlement. Free claim review.",
    keywords:
      "kin insurance claim denied, kin insurance underpaid claim florida, kin public adjuster florida, kin homeowners claim dispute",
    intro:
      "Kin is a direct-to-consumer, technology-focused homeowners insurer writing a growing number of Florida policies, including coastal and Tampa Bay properties. As with other digital-first carriers, claims are often handled remotely and with standardized estimating, which can leave gaps between the offer and the real cost of repair. Claim Beast re-documents the loss, prepares an independent estimate, and negotiates with Kin on your behalf.",
    points: [
      "Independent estimate to test the Kin offer",
      "In-person documentation of damage a remote inspection may miss",
      "Rebuttal of standardized underscoping and depreciation",
      "Supplement filing after the first payment",
      "Negotiation and escalation when the offer is short",
    ],
    denialReasons: [
      "Remote or standardized estimates that miss damage",
      "Improper depreciation",
      "Cause-of-loss disputes",
      "Sublimits and exclusions applied aggressively",
      "Documentation gaps",
    ],
    faqs: [
      {
        question: "Kin handled my claim remotely. Can you re-inspect in person?",
        answer:
          "Yes. We inspect the property in person and document damage that a remote or virtual inspection can miss, then prepare an independent estimate.",
      },
      {
        question: "My Kin payout was short. What can I do?",
        answer:
          "We identify the missing scope and file a supplement to recover it, negotiating directly with Kin.",
      },
      {
        question: "Can you dispute a Kin denial?",
        answer:
          "Yes. We review the denial and policy, document the covered loss, and dispute the stated reason.",
      },
      {
        question: "What does it cost?",
        answer:
          "Nothing upfront — contingency only, within Florida's fee caps.",
      },
    ],
  },

  "peoples-trust-insurance-claim-help-florida": {
    slug: "peoples-trust-insurance-claim-help-florida",
    carrier: "People's Trust Insurance",
    topic: "People's Trust",
    h1: "People's Trust Insurance Claim Help in Florida",
    metaTitle:
      "People's Trust Insurance Claim Denied or Underpaid? | Florida Public Adjuster | Claim Beast",
    metaDescription:
      "People's Trust Insurance claim underpaid or denied in Florida? Claim Beast public adjusters in Riverview, FL document the loss and negotiate the payout you're owed. Free claim review.",
    keywords:
      "peoples trust insurance claim denied, peoples trust underpaid claim florida, peoples trust public adjuster, peoples trust rapid response claim dispute",
    intro:
      "People's Trust is a Florida homeowners insurer known for its Rapid Response managed-repair program, which steers many claims to an affiliated contractor network. Managed repair can be convenient, but it can also cap the documented scope and leave homeowners unsure whether the repair fully addresses the loss. Claim Beast independently documents the full extent of your damage and negotiates with People's Trust so you can make an informed decision and receive a fair, complete settlement.",
    points: [
      "Independent documentation of the full loss, separate from managed repair",
      "Replacement-cost estimate to compare against the offered scope",
      "Documentation of hidden, matching, and code-required work",
      "Rebuttal of capped or underscoped managed-repair estimates",
      "Negotiation, supplements, and escalation as needed",
    ],
    denialReasons: [
      "Managed-repair scopes that cap the documented damage",
      "Underscoped estimates and improper depreciation",
      "Cause-of-loss disputes",
      "Sublimits and exclusions applied aggressively",
      "Incomplete documentation of hidden damage",
    ],
    faqs: [
      {
        question: "People's Trust wants to use its Rapid Response program. Do I have to?",
        answer:
          "Policy terms vary, but you always have the right to understand the full value of your loss. We document the complete scope independently so you can decide with full information rather than accepting a capped repair.",
      },
      {
        question: "My managed-repair scope seems incomplete. Can you help?",
        answer:
          "Yes. We prepare an independent estimate, identify what the managed-repair scope left out, and negotiate to have it addressed.",
      },
      {
        question: "Can you dispute a People's Trust denial?",
        answer:
          "Yes. We review the denial and policy, document the covered loss, and dispute the stated reason with evidence.",
      },
      {
        question: "What will this cost me?",
        answer:
          "Nothing upfront — contingency only, within Florida's statutory fee caps.",
      },
    ],
  },
};
